import { pollKeys } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/api";
import type {
  PollOptionType,
  PollResponse,
} from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/api";
import { usePollVoteListModal } from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollVoteListModal";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Typography } from "@circleco/compass/components/Typography";
import { isEmpty } from "lodash";
import { useMutation, useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";

interface PollOptionProps {
  canRespond: boolean;
  option: PollOptionType;
  poll: PollResponse;
  selectedOptionId?: number;
  setSelectedOptionId: (id: number) => void;
  shouldShowResults: boolean;
}

export const PollOption = ({
  option,
  selectedOptionId,
  setSelectedOptionId,
  canRespond,
  poll,
  shouldShowResults,
}: PollOptionProps) => {
  const queryClient = useQueryClient();
  const { show: showPollVoteListModal } = usePollVoteListModal();
  const respondMutation = useMutation({
    mutationFn: (pollOptionId: number) =>
      reactQueryPost(internalApi.polls.respond(poll.id), {
        poll_option_id: pollOptionId,
      }),
    onSuccess: results => {
      if (isEmpty(results)) return;
      queryClient.setQueryData(
        pollKeys.detail(poll.sgid),
        (oldData: PollResponse | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            community_member_response_option_id: option.id,
            results,
          };
        },
      );
    },
  });

  const handleClick = () => {
    if (canRespond) {
      setSelectedOptionId(option.id);
    }
    respondMutation.mutate(option.id);
  };

  const percentage =
    poll.results?.detailed_results?.find(
      result => result.poll_option_id === option.id,
    )?.percentage ?? 0;
  const isActive = selectedOptionId === option.id;
  const isClosed = poll.status === "closed";
  const shouldDisable = !canRespond || isClosed || !!selectedOptionId;

  return (
    <div
      className={classNames(
        "relative isolate flex w-full items-stretch overflow-hidden rounded-lg border",
        {
          "border-dark bg-secondary ring-1 ring-inset ring-[var(--color-border-dark)]":
            isActive,
          "border-primary bg-primary": !isActive,
          "hover:border-dark hover:bg-secondary": !shouldDisable && !isActive,
        },
      )}
      data-testid="poll-option"
    >
      {shouldShowResults && (
        <div
          className="bg-tertiary absolute inset-y-0 left-0"
          style={{ width: `${percentage}%` }}
        />
      )}
      <button
        type="button"
        disabled={shouldDisable}
        onClick={handleClick}
        className="relative z-10 flex min-w-0 flex-1 cursor-pointer items-start gap-2 px-5 py-4 text-left disabled:cursor-not-allowed"
        data-testid="select-poll-option"
      >
        {!isClosed && (
          <div
            className={classNames(
              "bg-primary mt-1 flex size-4 shrink-0 items-center justify-center rounded-full border",
              isActive ? "border-dark" : "border-primary",
            )}
          >
            {isActive && (
              <div className="bg-dark-primary-button size-2 rounded-full" />
            )}
          </div>
        )}
        <div className="min-w-0 flex-1 text-left">
          <Typography
            className="whitespace-normal break-words"
            component="span"
            variant="body-sm"
          >
            {option.value}
          </Typography>
        </div>
        {shouldShowResults && (
          <Typography
            className="mt-1 shrink-0"
            color="secondary"
            component="span"
            data-testid="poll-option-info"
            variant="body-sm"
          >
            {percentage}%
          </Typography>
        )}
      </button>
      {shouldShowResults && (
        <IconButton
          aria-label={t("tiptap.extensions.poll.view_responses")}
          className="relative z-10 mr-3 mt-3.5 shrink-0 self-start"
          data-testid="open-poll-option-info"
          icon="chevron-right"
          onClick={() =>
            void showPollVoteListModal({
              pollId: poll.id,
              pollOptionId: option.id,
              pollValue: option.value,
              percentage,
            })
          }
          size="sm"
          type="button"
          variant="ghost"
        />
      )}
    </div>
  );
};
